import * as React from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepConnector, {
  stepConnectorClasses,
} from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import { ReactComponent as VendorIcon } from '../../assets/icons/VendorHome.svg';
import { ReactComponent as VendorRejectedIcon } from '../../assets/icons/VendorCancel.svg';
import { ReactComponent as PotIcon } from '../../assets/icons/CookingPot.svg';
import { ReactComponent as CartBagIcon } from '../../assets/icons/CartBag.svg';
import { ReactComponent as CheckIcon } from '../../assets/icons/Check.svg';
import { ReactComponent as RefundReturnIcon } from '../../assets/icons/RefundReturnIcon.svg';
import { ReactComponent as CrossMarkIcon } from '../../assets/icons/CrossMarkIcon.svg';
import { OrderStatus, History } from '../../types/order';
import { orderStatusConfig } from '../vendor-order-card';
const wasReadyOrAssignedOrPicked = (status?: OrderStatus): boolean =>
  status === OrderStatus.AT_COLLECTION_POINT ||
  status === OrderStatus.READY_TO_COLLECT ||
  status === OrderStatus.ASSIGNED ||
  status === OrderStatus.PICKED ||
  status === OrderStatus.REFUNDED;

const wasPreparingOrAccepted = (status?: OrderStatus): boolean =>
  status === OrderStatus.PREPARING || status === OrderStatus.ACCEPTED || status === OrderStatus.ASSIGNED_BUT_PREPARING;

const wasOrderReadyForCollection = (status?: OrderStatus): boolean =>
  status === OrderStatus.PREPARED ||
  status === OrderStatus.AT_COLLECTION_POINT ||
  status === OrderStatus.READY_TO_COLLECT;

const orderStatusSteps: Partial<Record<OrderStatus, string>> = {
  [OrderStatus.ACCEPTED]: 'Accepted',
  [OrderStatus.PREPARING]: 'Preparing Food',
  [OrderStatus.PREPARED]: 'Prepared',
  [OrderStatus.DELIVERED]: 'Delivered',
};
const getStepIconByStatus = (
  stepIndex: number,
  orderStatus: OrderStatus,
  previousOrderStatus?: OrderStatus,
): React.ReactElement => {
  const isRefunded = orderStatus === OrderStatus.REFUNDED;
  const isDeliveredButRefunded = orderStatus === OrderStatus.DELIVERED_BUT_REFUNDED

  switch (stepIndex) {
    case 1:
      return orderStatus === OrderStatus.REJECTED ? (
        <VendorRejectedIcon />
      ) : (
        <VendorIcon />
      );

    case 2:
      return wasPreparingOrAccepted(previousOrderStatus) && isRefunded ? (
        <RefundReturnIcon />
      ) : (
        <PotIcon />
      );

    case 3:
      if (
        (previousOrderStatus === OrderStatus.PREPARED || 
          wasReadyOrAssignedOrPicked(previousOrderStatus)) &&
        (isRefunded || isDeliveredButRefunded)
      ) {
        return <RefundReturnIcon />;
      }
      if (
        (wasPreparingOrAccepted(previousOrderStatus) ||
          wasReadyOrAssignedOrPicked(previousOrderStatus)) &&
        isRefunded
      ) {
        return <CrossMarkIcon />;
      }
      return <CartBagIcon />;

    case 4:
      if (
        previousOrderStatus === OrderStatus.DELIVERED ||
        (previousOrderStatus === OrderStatus.COMPLETE && isDeliveredButRefunded)
      ) {
        return <RefundReturnIcon />;
      }
      if (
        (wasPreparingOrAccepted(previousOrderStatus) || wasReadyOrAssignedOrPicked(previousOrderStatus) ||
          wasOrderReadyForCollection(previousOrderStatus)) &&
        (isRefunded || isDeliveredButRefunded)
      ) {
        return <CrossMarkIcon />;
      }
      return <CheckIcon />;

    default:
      return <></>;
  }
};
const getRefundStepIndex = (prevOrderStatus: string | undefined): number => {
  if (wasPreparingOrAccepted(prevOrderStatus as OrderStatus)) {
    return 2;
  } else if (
    prevOrderStatus === OrderStatus.PREPARED ||
    wasReadyOrAssignedOrPicked(prevOrderStatus as OrderStatus)
  ) {
    return 3;
  } else if (
    prevOrderStatus === OrderStatus.DELIVERED ||
    prevOrderStatus === OrderStatus.COMPLETE || prevOrderStatus === OrderStatus.DELIVERED_BUT_REFUNDED
  ) {
    return 4;
  }
  return 0;
};

const shouldShowRefundStyling = (
  currentOrderStatus: string,
  prevOrderStatus: string | undefined,
  currentIndex: number,
  isStepIncluded: boolean,
): boolean => {
  if (currentOrderStatus !== OrderStatus.REFUNDED && currentOrderStatus !== OrderStatus.DELIVERED_BUT_REFUNDED) {
    return false;
  }

  const refundStepIndex = getRefundStepIndex(prevOrderStatus);
  return isStepIncluded
    ? currentIndex <= refundStepIndex
    : currentIndex < refundStepIndex;
};

const ColorlibConnector = styled(StepConnector)<{
  orderStatus: string;
  previousOrderStatus?: string;
  connectorIndex: number;
}>(({ theme, orderStatus, previousOrderStatus, connectorIndex }) => {
  const isRefundConnection = shouldShowRefundStyling(
    orderStatus,
    previousOrderStatus,
    connectorIndex,
    false,
  );

  return {
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
      top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: isRefundConnection ? '#F84244' : 'rgb(0,0,0)',
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        backgroundColor: isRefundConnection ? '#F84244' : 'rgb(0,0,0)',
      },
    },
    [`& .${stepConnectorClasses.line}`]: {
      height: 3,
      border: 0,
      backgroundColor: isRefundConnection
        ? '#F84244'
        : theme.palette.mode === 'dark'
        ? theme.palette.grey[800]
        : '#eaeaf0',
      borderRadius: 1,
    },
  };
});

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
  orderStatus: string;
  previousOrderStatus?: string;
  stepIndex: number;
}>(({ theme, ownerState, orderStatus, previousOrderStatus, stepIndex }) => {
  const shouldShowRefundIconStyling = shouldShowRefundStyling(
    orderStatus,
    previousOrderStatus,
    stepIndex,
    true,
  );

  return {
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
    zIndex: 1,
    color: '#fff',
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    filter: 'grayscale(100%)',
    ...(ownerState.active && {
      backgroundColor: orderStatusConfig[orderStatus]?.backgroundColor,
      boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
      filter: 'grayscale(0%)',
    }),
    ...(ownerState.completed && {
      backgroundColor: 'rgb(0,0,0)',
      filter: 'grayscale(0%)',
    }),
    ...(shouldShowRefundIconStyling && {
      backgroundColor: '#F84244',
      boxShadow: '0 4px 10px 0 rgba(248, 66, 68, 0.4)',
      filter: 'grayscale(0%)',
    }),
  };
});

interface IColorlibStepIcon extends StepIconProps {
  orderStatus: OrderStatus;
  previousOrderStatus: OrderStatus;
}

const ColorlibStepIcon = (props: IColorlibStepIcon) => {
  const {
    active,
    completed,
    className,
    orderStatus,
    icon,
    previousOrderStatus,
  } = props;
  const getStepIcon = (index: number): React.ReactElement => {
    return getStepIconByStatus(index, orderStatus, previousOrderStatus);
  };

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
      orderStatus={orderStatus}
      previousOrderStatus={previousOrderStatus}
      stepIndex={Number(icon)}
    >
      {getStepIcon(Number(icon))}
    </ColorlibStepIconRoot>
  );
};
interface IOrderStatusStepper {
  orderStatus: OrderStatus;
  previousOrderStatus?: string;
}
export const OrderStatusStepper = ({
  orderStatus,
  previousOrderStatus,
}: IOrderStatusStepper) => {
  const stepEntries = Object.entries(orderStatusSteps);
  return (
    <Stack sx={{ width: '100%' }}>
      <Stepper
        alternativeLabel
        activeStep={orderStatusConfig[orderStatus]?.stepperIndex}
        connector={<div />}
      >
        {stepEntries.map(([status], index) => (
          <Step key={status}>
            <StepLabel
              StepIconComponent={(props) => (
                <ColorlibStepIcon
                  orderStatus={orderStatus}
                  previousOrderStatus={previousOrderStatus}
                  {...props}
                />
              )}
            >
              {index > 0 && (
                <ColorlibConnector
                  orderStatus={orderStatus}
                  previousOrderStatus={previousOrderStatus}
                  connectorIndex={index}
                />
              )}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Stack>
  );
};
